import moment from "moment";
import { DATE_FORMATS } from "../constants";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

export const isAfterToday = (date) => {
  const today = moment.utc().startOf("day");
  const formattedDate = moment.utc(date).startOf("day");
  return formattedDate.isAfter(today);
};

export const getValidDates = (activeExercises) => {
  const dates = [];

  const currDate = moment.utc().startOf("month").subtract(6, "months");
  const lastDate = moment.utc().endOf("month").add(6, "months");

  while (currDate.add(1, "days").diff(lastDate) < 0) {
    const formattedCurrentDate = moment.utc(currDate).format(DATE_DAY_FORMAT);
    dates[formattedCurrentDate] = true;
  }

  activeExercises.map((exercise) => {
    const formatted = moment
      .utc((exercise.utcDate && exercise.utcDate.toString()) || exercise.date)
      .format(DATE_DAY_FORMAT);
    dates[formatted] = false;
  });

  return dates;
};

export const getDisplayDate = (date, isUtc = false) => {
  if (isUtc) {
    return moment(String(date)).format("MMM D, YYYY");
  }

  return moment(date).format("MMM D, YYYY");
};

/**
 * Function to get the days of the week of an interval date
 * @param {*} startDate
 * @param {*} endDate
 * @returns
 */
export const calculateWeeksWithDays = (startDate, endDate) => {
  const start = moment.utc(startDate, "YYYYMMDD");
  const end = moment.utc(endDate, "YYYYMMDD");

  if (!start.isValid() || !end.isValid()) {
    throw new Error("The formate date must be YYYYMMDD.");
  }

  if (start.isAfter(end)) {
    throw new Error("The start date must be earlier than the end date");
  }

  const weeks = [];
  let currentStart = start.clone(); // Start in the startDate

  while (currentStart.isBefore(end) || currentStart.isSame(end, "day")) {
    const week = { days: [] };

    // If it's the first week wee start from `startDate`
    const weekEnd = moment.min(currentStart.clone().endOf("isoWeek"), end); // until sunday or endDate

    while (
      currentStart.isBefore(weekEnd) ||
      currentStart.isSame(weekEnd, "day")
    ) {
      week.days.push({
        date: currentStart.clone().utc().format("YYYYMMDD"), // Format as YYYYMMDD in UTC
        meals: [
          { type: "Breakfast", order: 1, foods: [] },
          { type: "Lunch", order: 2, foods: [] },
          { type: "Snack", order: 3, foods: [] },
          { type: "Dinner", order: 4, foods: [] },
        ],
      });
      currentStart.add(1, "day");
    }

    weeks.push(week);
  }

  return weeks;
};

/**
 * Function to get the start and end date of several weeks
 * @param {*} weeks
 * @returns
 */
export const calculateStartAndEndDateOfTheWeeks = (weeks) => {
  return weeks
    .map(({ days }) => {
      if (!Array.isArray(days) || days.length === 0) {
        return null;
      }
      const startDate = days[0].date;
      const endDate = days[days.length - 1].date;
      const isCompleted = days.every(
        (day) =>
          Array.isArray(day.meals) &&
          day.meals.every(
            (meal) => Array.isArray(meal.foods) && meal.foods.length > 0
          )
      );
      return { startDate, endDate, isCompleted };
    })
    .filter(Boolean);
};

/**
 * Helper to create an UTC date from yyymmdd
 * @param {*} yyyymmdd
 * @returns
 */
const createUTCDateFromYMD = (yyyymmdd) => {
  const str = String(yyyymmdd);

  const year = parseInt(str.substring(0, 4), 10);

  const month = parseInt(str.substring(4, 6), 10) - 1;

  const day = parseInt(str.substring(6, 8), 10);

  return new Date(Date.UTC(year, month, day));
};

/**
 * Helper to convert an utc date to yyymmdd
 * @param {*} date
 * @returns
 */
const formatDateToYMD = (date) => {
  const year = date.getUTCFullYear();

  const month = String(date.getUTCMonth() + 1).padStart(2, "0");

  const day = String(date.getUTCDate()).padStart(2, "0");

  return parseInt(`${year}${month}${day}`, 10);
};

/**
 * Function to know the days of the week and get the days who has data
 * @param {*} week
 * @param {*} activeExercises
 * @param {*} selectedNutritionPlan
 * @returns
 */
export const completeWeek = (
  week,
  activeExercises = [],
  selectedNutritionPlan
) => {
  const datesWithData = new Set(week.map((day) => day.date));

  // Calculate the monday of the week
  const firstDayOfWeek = createUTCDateFromYMD(week[0].date);
  const dayOfWeek = firstDayOfWeek.getUTCDay(); // 0 = sunday, 1 = monday, ..., 6 = saturday
  const offset = (dayOfWeek + 6) % 7; // Monday should be the 0

  firstDayOfWeek.setUTCDate(firstDayOfWeek.getUTCDate() - offset);

  // Generate the 7 days
  const fullWeek = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(firstDayOfWeek);
    date.setUTCDate(firstDayOfWeek.getUTCDate() + i);

    const utcDate = formatDateToYMD(date);
    const dayData = week.find((d) => d.date === utcDate) || {
      macrosTotal: {
        calories: 0,
        carbs: 0,
        fats: 0,
        proteins: 0,
      },
    };
    const macrosTotal = dayData?.macrosTotal;

    const {
      calories = 0,
      carbs = 0,
      fats = 0,
      proteins = 0,
      restDay,
    } = selectedNutritionPlan;
    const goals = (() => {
      if (!restDay) {
        return {
          calories,
          carbs,
          fats,
          proteins,
        };
      }

      const isTrainingDay = activeExercises.some(
        (exercise) => exercise.utcDate == utcDate
      );

      return isTrainingDay
        ? {
            calories,
            carbs,
            fats,
            proteins,
          }
        : restDay;
    })();

    const hasFoods =
      Array.isArray(dayData.meals) &&
      dayData.meals.some(
        (meal) => Array.isArray(meal.foods) && meal.foods.length > 0
      );

    fullWeek.push({
      date: utcDate,
      hasData: datesWithData.has(utcDate),
      hasFoods,
      macrosTotal,
      goals,
    });
  }

  return fullWeek;
};

/**
 * Function to know the days of the week of a meal plan template
 * @param {*} week
 * @returns
 */
export const completeTemplateWeek = (week) => {
  // Generate the 7 days
  const fullWeek = [];
  for (let i = 0; i < 7; i++) {
    const utcDate = `Day ${i + 1}`;
    const currentWeek = week[i];
    const macrosTotal = currentWeek || {
      macrosTotal: {
        calories: 0,
        carbs: 0,
        fats: 0,
        proteins: 0,
      },
    };
    const goals = {
      calories: 0,
      carbs: 0,
      fats: 0,
      proteins: 0,
    };

    const hasData = !!currentWeek;

    const hasFoods =
      Array.isArray(currentWeek.meals) &&
      currentWeek.meals.some(
        (meal) => Array.isArray(meal.foods) && meal.foods.length > 0
      );

    fullWeek.push({
      date: utcDate,
      hasData,
      hasFoods,
      macrosTotal,
      goals,
    });
  }

  return fullWeek;
};

/**
 * This function is going to return something like Feb 12, Fri
 * @param {*} utcDate
 * @returns
 */
export const getDateWithDayToDisplay = (utcDate) => {
  const isUtcFormat = moment.utc(utcDate, moment.ISO_8601, true).isValid();
  return isUtcFormat ? moment(String(utcDate)).format("MMM D, ddd") : utcDate;
};
